import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Mic, Sparkles, CheckCircle2, Clock, ShieldCheck, CalendarDays } from "lucide-react";
import { HospitalHeader, BrandedShell } from "@/components/BrandedLayout";
import { RecoveryCalendar } from "@/components/RecoveryCalendar";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { progressForDay, formatDate } from "@/lib/appUtils";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DAILY_LIMIT_SECONDS = 20 * 60;
const PER_RESPONSE_SECONDS = 30;

export default function PatientPortal() {
  const { patientId } = useParams();
  const { hospital } = useBranding();
  const [patient, setPatient] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answered, setAnswered] = useState({});
  const [recordingIdx, setRecordingIdx] = useState(null);
  const [secondsUsed, setSecondsUsed] = useState(0);

  useEffect(() => {
    api.getPatient(patientId).then((p) => {
      setPatient(p);
      setSecondsUsed(p.check_in_seconds_used || 0);
      api.getQuestions(p.specialty).then((r) => setQuestions(r.questions));
    });
  }, [patientId]);

  const today = patient?.recovery_day || 1;
  const totalDays = patient?.care_plan_duration || 20;
  const todayProgress = useMemo(
    () => progressForDay(patient?.tasks || [], today),
    [patient, today]
  );

  const onToggleTask = async (taskId, done) => {
    if (!patient) return;
    setPatient((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, done } : t)),
    }));
    try { await api.updateTask(patient.id, taskId, { done }); }
    catch { toast.error("Failed to update task"); }
  };

  const record = (idx) => {
    if (recordingIdx !== null) return;
    if (secondsUsed + PER_RESPONSE_SECONDS > DAILY_LIMIT_SECONDS) {
      toast.error("Daily 20-minute voice limit reached", {
        description: "Please continue tomorrow — your care team has your data.",
      });
      return;
    }
    setRecordingIdx(idx);
    setTimeout(async () => {
      const { text } = await api.transcribe();
      setAnswered((a) => ({ ...a, [idx]: text }));
      setSecondsUsed((s) => s + PER_RESPONSE_SECONDS);
      await api.checkin(patientId, PER_RESPONSE_SECONDS);
      setRecordingIdx(null);
    }, 2000);
  };

  if (!patient) return <BrandedShell><div className="text-muted-foreground">Loading…</div></BrandedShell>;

  const answeredCount = Object.keys(answered).length;
  const progressPct = Math.round((answeredCount / (questions.length || 1)) * 100);
  const minutesUsed = Math.floor(secondsUsed / 60);

  return (
    <BrandedShell>
      <HospitalHeader subtitle="Your recovery journey" testId="patient-header" />

      {/* Hero */}
      <div className="surface-card p-6 sm:p-8 mb-6 shadow-sm animate-fade-in-up">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">Welcome back</div>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Hi {patient.name.split(" ")[0]}</h2>
            <div className="text-sm text-muted-foreground mt-2">
              {patient.surgery_type} · Surgery on {formatDate(patient.surgery_date)}
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary">
                <CalendarDays className="w-3 h-3 mr-1" /> Day {today} of {totalDays}
              </span>
              <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">
                {hospital?.name}
              </span>
              <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border brand-border brand-bg-soft brand-text">
                <ShieldCheck className="w-3 h-3 mr-1" /> Private link · No login
              </span>
            </div>
          </div>

          <CircularProgress value={todayProgress.pct} label={`${todayProgress.done}/${todayProgress.total}`} sub="today" />
        </div>
      </div>

      {/* Daily check-in — voice UI preserved */}
      <div className="surface-card p-6 sm:p-8 mb-8 shadow-sm" data-testid="daily-checkin">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 brand-text" />
              <h3 className="text-lg font-semibold">Daily Check-in</h3>
            </div>
            <div className="text-sm text-muted-foreground">Answer today's questions with your voice.</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary">
              <Clock className="w-3 h-3 mr-1" /> 30s per response
            </span>
            <div className="min-w-[220px]">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1 font-medium">
                <span>Voice minutes today</span>
                <span>{minutesUsed}m / 20m</span>
              </div>
              <Progress value={(secondsUsed / DAILY_LIMIT_SECONDS) * 100} className="h-1.5" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {questions.map((q, idx) => {
            const isRec = recordingIdx === idx;
            const isDone = !!answered[idx];
            return (
              <div key={idx}
                data-testid={`question-card-${idx}`}
                className={cn(
                  "rounded-lg border p-4 flex items-start gap-3 transition-all",
                  isDone ? "border-border bg-secondary/50" : "border-border bg-card hover:border-foreground/20"
                )}>
                <button
                  onClick={() => record(idx)}
                  disabled={isRec}
                  data-testid={`mic-btn-${idx}`}
                  className={cn(
                    "relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all shadow-sm border",
                    isRec
                      ? "bg-rose-600 text-white border-rose-500 ring-4 ring-rose-500/20"
                      : isDone
                      ? "bg-foreground text-background border-foreground"
                      : "bg-card text-foreground border-border hover:bg-secondary"
                  )}
                  aria-label="Record response">
                  {isRec && <span className="absolute inset-0 rounded-full bg-rose-500/30 recording-ping" />}
                  {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{q}</div>
                  {isRec && (
                    <div className="text-xs text-rose-600 dark:text-rose-400 mt-1 inline-flex items-center gap-1 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" /> Listening…
                    </div>
                  )}
                  {isDone && (
                    <div className="text-xs text-muted-foreground mt-1 italic">"{answered[idx]}"</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
          <Progress value={progressPct} className="h-1 flex-1" />
          <span className="font-medium text-foreground">{answeredCount}/{questions.length} answered</span>
        </div>
      </div>

      {/* Recovery Calendar */}
      <div className="mb-3 flex items-center gap-2">
        <CalendarDays className="w-4 h-4 brand-text" />
        <h3 className="text-lg font-semibold">Your recovery plan · {totalDays} days</h3>
        <span className="text-xs text-muted-foreground ml-2">Check off tasks as you complete them.</span>
      </div>

      <RecoveryCalendar
        tasks={patient.tasks || []}
        currentDay={today}
        totalDays={totalDays}
        interactive
        onToggle={onToggleTask}
      />
    </BrandedShell>
  );
}

function CircularProgress({ value, label, sub }) {
  const radius = 42;
  const c = 2 * Math.PI * radius;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative w-24 h-24 shrink-0" data-testid="today-progress">
      <svg width="96" height="96" viewBox="0 0 112 112" className="-rotate-90">
        <circle cx="56" cy="56" r={radius} strokeWidth="6" stroke="hsl(var(--border))" fill="none" />
        <circle
          cx="56" cy="56" r={radius}
          strokeWidth="6"
          stroke="var(--brand-primary)"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-base font-bold">{label}</div>
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{sub}</div>
      </div>
    </div>
  );
}
